import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const LINKS = [
  ["Junk Removal", "#services"],
  ["Hauling", "#hauling"],
  ["Our Work", "#proof"],
  ["Reviews", "#reviews"],
  ["Pricing", "#pricing"],
  ["FAQ", "#faq"],
  ["Contact", "#contact"],
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 z-50 w-full bg-ink transition-shadow duration-300 ${
        scrolled ? "border-b-2 border-gold/40 shadow-[0_6px_0_rgba(0,0,0,0.6)]" : "border-b-2 border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 md:h-20 md:px-8">
        <a href="#top" data-testid="nav-logo-link" aria-label="Godspeed Junk Removal and Hauling — home" className="flex items-center">
          <img src="/assets/logo-new.png" alt="Godspeed Junk Removal & Hauling" data-testid="nav-logo" className="h-8 w-auto md:h-11" />
        </a>
        <nav className="hidden items-center gap-4 whitespace-nowrap lg:flex xl:gap-6" aria-label="Primary">
          <a
            href="#top"
            data-testid="nav-link-home"
            className="relative font-body text-[13px] font-bold uppercase tracking-[0.1em] text-gold xl:text-sm xl:tracking-[0.15em]"
          >
            Home
            <span className="absolute -bottom-1.5 left-0 h-[3px] w-full bg-gold" />
          </a>
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative whitespace-nowrap font-body text-[13px] font-bold uppercase tracking-[0.1em] text-white/70 xl:text-sm xl:tracking-[0.12em] transition-colors duration-200 hover:text-gold"
            >
              {label}
              <span className="absolute -bottom-1.5 left-0 h-[3px] w-full origin-left scale-x-0 bg-gold transition-transform duration-200 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="tel:4706840150"
            data-testid="nav-phone-chip"
            className="hidden items-center gap-2 whitespace-nowrap border-2 border-gold/50 px-3.5 py-2 font-body text-sm font-bold text-white transition-colors duration-200 hover:border-gold hover:text-gold md:flex lg:hidden xl:flex"
          >
            <Phone size={15} className="text-gold" />
            470-684-0150
          </a>
          <a
            href="#contact"
            data-testid="nav-quote-button"
            className="btn-brutal shadow-hard-sm whitespace-nowrap bg-gold px-5 py-2.5 font-heading text-lg uppercase tracking-wide text-ink"
          >
            Get a Free Quote
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-1 text-gold lg:hidden"
            aria-label="Toggle navigation menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="mobile-menu"
            className="fixed inset-0 top-16 z-[-1] flex flex-col justify-center bg-ink px-8 lg:hidden"
          >
            {LINKS.map(([label, href], i) => (
              <motion.a
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 + i * 0.05, duration: 0.25 }}
                data-testid={`mobile-nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-b-2 border-coal py-4 font-heading text-5xl uppercase tracking-wide text-white transition-colors duration-200 hover:text-gold"
              >
                {label}
              </motion.a>
            ))}
            <div className="mt-8 flex gap-3">
              <a
                href="tel:4706840150"
                data-testid="mobile-nav-call"
                className="flex-1 border-2 border-gold px-5 py-4 text-center font-heading text-xl uppercase tracking-wide text-gold"
              >
                Call
              </a>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-quote"
                className="flex-1 bg-gold px-5 py-4 text-center font-heading text-xl uppercase tracking-wide text-ink"
              >
                Book Now
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
